import { useEffect, useState } from "react";

export type FontFaceState = "unsupported" | "loading" | "ready" | "failed";

/** jsdom and some older browsers have no FontFace API; the sample is skipped there. */
export const fontFaceSupported = () => typeof FontFace !== "undefined" && typeof document !== "undefined" && !!document.fonts;

/** Registers a fetched font file under `family` for this page, and removes it on cleanup. */
export function useFontFace(family: string, file: Blob | undefined): FontFaceState {
  const [state, setState] = useState<FontFaceState>(fontFaceSupported() ? "loading" : "unsupported");
  useEffect(() => {
    if (!file || !fontFaceSupported()) return;
    let cancelled = false;
    let added: FontFace | null = null;
    setState("loading");
    // Starting from a resolved promise turns a synchronous throw into the failed state too.
    Promise.resolve()
      .then(() => file.arrayBuffer())
      .then((data) => new FontFace(family, data).load())
      .then((face) => {
        if (cancelled) return;
        document.fonts.add(face);
        added = face;
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
      if (added) document.fonts.delete(added);
    };
  }, [family, file]);
  return state;
}
